/**
 * QuickstartMobileThankYou
 *
 * Mobile thank-you screen. Renders the same Icon Drop UI shell as the desktop
 * thank-you path (shared QuickstartThankYouStage); the old add-to-home-screen
 * instruction card is retired.
 *
 * CTA behaviour follows this branch's WebView contract: the resolved outbound
 * URL is handed to the shell as a real anchor href (embedded WebViews block
 * window.open), with the analytics callback firing on click before navigation.
 * Auto-redirect still uses location.href after 500ms.
 */

import { useCallback, useEffect, useRef } from 'react';
import { QuickstartThankYouStage } from './QuickstartThankYouStage';
import { QuickstartTheme, getResolvedCtaUrl } from '../types/theme';
import { getOutboundCtaUrl } from '../utils/steamUrls';

interface QuickstartMobileThankYouProps {
  theme: QuickstartTheme;
  hasSlugConfig: boolean;
  autoRedirect?: boolean;
  onRedirect?: (steamUrl: string) => void;
}

export const QuickstartMobileThankYou = ({ theme, hasSlugConfig, autoRedirect, onRedirect }: QuickstartMobileThankYouProps) => {
  // Resolved CTA destination as a real link so embedded WebViews can navigate.
  const ctaHref = (() => {
    const ctaUrl = getResolvedCtaUrl(theme);
    return ctaUrl ? getOutboundCtaUrl(ctaUrl, hasSlugConfig) : undefined;
  })();

  const hasAutoRedirected = useRef(false);

  const handleCheckItOut = useCallback(() => {
    // Analytics first - the browser navigation below is what embedded WebViews
    // (AppLovin/LoopMe, etc.) honor; window.open is blocked inside them.
    if (ctaHref) {
      onRedirect?.(ctaHref);
    }
  }, [ctaHref, onRedirect]);

  useEffect(() => {
    if (!autoRedirect || hasAutoRedirected.current) {
      return;
    }

    const ctaUrl = getResolvedCtaUrl(theme);
    if (!ctaUrl) {
      return;
    }

    hasAutoRedirected.current = true;

    // Auto-redirect: Use location.href instead of window.open to avoid popup blockers
    const finalUrl = getOutboundCtaUrl(ctaUrl, hasSlugConfig);

    // Send analytics callback before redirect
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Delay to ensure analytics can be sent
    setTimeout(() => {
      window.location.href = finalUrl;
    }, 500);
  }, [autoRedirect, onRedirect, theme, hasSlugConfig]);

  return (
    <QuickstartThankYouStage
      theme={theme}
      ctaHref={ctaHref}
      onCta={ctaHref ? handleCheckItOut : undefined}
      isMobile
      forcePortrait
    />
  );
};
